import React, { useState, lazy, Component } from 'react';
import { Palette, Type, LayoutGrid, MousePointer2, Square, Table as TableIcon, FormInput, AlertCircle, BarChart3, Navigation, BoxSelect, Image as ImageIcon, Ruler, Grid3x3, Sun, Accessibility, CheckCircle2, AlertTriangle, Info, XCircle, Search, ChevronDown, Download, MoreVertical, FileText, User, Settings, Bell, Menu, ArrowRight, Plus, Trash2, Edit2, Sparkles, ToggleLeft, ToggleRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
type Section = 'typography' | 'colors' | 'buttons' | 'cards' | 'tables' | 'forms' | 'alerts' | 'charts' | 'navigation' | 'empty-states' | 'icons' | 'spacing' | 'grid' | 'theme' | 'accessibility';
export function DesignSystem() {
  const [activeSection, setActiveSection] = useState<Section>('typography');
  const sections = [{
    id: 'typography',
    label: 'Typography',
    icon: Type,
    category: 'Foundations'
  }, {
    id: 'colors',
    label: 'Colors',
    icon: Palette,
    category: 'Foundations'
  }, {
    id: 'spacing',
    label: 'Spacing',
    icon: Ruler,
    category: 'Foundations'
  }, {
    id: 'grid',
    label: 'Responsive Grid',
    icon: Grid3x3,
    category: 'Foundations'
  }, {
    id: 'icons',
    label: 'Icons',
    icon: ImageIcon,
    category: 'Foundations'
  }, {
    id: 'buttons',
    label: 'Buttons',
    icon: MousePointer2,
    category: 'Components'
  }, {
    id: 'cards',
    label: 'Cards',
    icon: Square,
    category: 'Components'
  }, {
    id: 'tables',
    label: 'Tables',
    icon: TableIcon,
    category: 'Components'
  }, {
    id: 'forms',
    label: 'Forms',
    icon: FormInput,
    category: 'Components'
  }, {
    id: 'alerts',
    label: 'Alerts',
    icon: AlertCircle,
    category: 'Components'
  }, {
    id: 'empty-states',
    label: 'Empty States',
    icon: BoxSelect,
    category: 'Components'
  }, {
    id: 'charts',
    label: 'Charts',
    icon: BarChart3,
    category: 'Patterns'
  }, {
    id: 'navigation',
    label: 'Navigation',
    icon: Navigation,
    category: 'Patterns'
  }, {
    id: 'theme',
    label: 'Light Theme',
    icon: Sun,
    category: 'Guidelines'
  }, {
    id: 'accessibility',
    label: 'Accessibility',
    icon: Accessibility,
    category: 'Guidelines'
  }] as const;
  const groupedSections = sections.reduce((acc, section) => {
    if (!acc[section.category]) {
      acc[section.category] = [];
    }
    acc[section.category].push(section);
    return acc;
  }, {} as Record<string, (typeof sections)[number][]>);
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-1367" data-mp="21g5ibwfpdiq6">
      {/* Header */}
      <div data-id="element-1368" data-mp="1wh2cwavt9aho">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-1369" data-mp="1whap00k40brt">
          <Palette className="h-6 w-6 mr-3 text-slate-700" data-id="element-1370" data-mp="1wf805jup0ooo" />
          Design System
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-1371" data-mp="2aaa3q0c2i1yb">
          Living style guide and component library for Oversight Insights.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-8" data-id="element-1372" data-mp="1ighwtmha5srv">
        {/* Left Sidebar Nav */}
        <div className="w-full md:w-64 shrink-0" data-id="element-1373" data-mp="1diyg8fx413wr">
          <nav className="space-y-6 sticky top-6" data-id="element-1374" data-mp="1dk9kegugwdoe">
            {Object.entries(groupedSections).map(([category, items]) => <div key={category} data-id="element-1375" data-mp="1mj4pfplrzhq4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-3" data-id="element-1376" data-mp="1mhtpk0vgau4a">
                  {category}
                </h3>
                <div className="space-y-1" data-id="element-1377" data-mp="2xy1rc03261ew">
                  {items.map(item => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return <button key={item.id} onClick={() => setActiveSection(item.id)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-1378" data-mp="213w6si1hf0g">
                        <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-1379" data-mp="l0hv7oumlfxt" />
                        {item.label}
                      </button>;
              })}
                </div>
              </div>)}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-1380" data-mp="znuk9njn63sy">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[600px]" data-id="element-1381" data-mp="13qhchpk85qqo">
            {activeSection === 'typography' && <TypographySection data-id="element-1382" data-mp="2o773fz6vo9xo" />}
            {activeSection === 'colors' && <ColorsSection data-id="element-1383" data-mp="2k6wymputnpke" />}
            {activeSection === 'buttons' && <ButtonsSection data-id="element-1384" data-mp="2f8d8ed973rm8" />}
            {activeSection === 'cards' && <CardsSection data-id="element-1385" data-mp="2f8vctx60hc58" />}
            {activeSection === 'tables' && <TablesSection data-id="element-1386" data-mp="2aaboxr7uxqa8" />}
            {activeSection === 'forms' && <FormsSection data-id="element-1387" data-mp="37xat0n0vq0i5" />}
            {activeSection === 'alerts' && <AlertsSection data-id="element-1388" data-mp="37wr9j1vkz420" />}
            {activeSection === 'charts' && <ChartsSection data-id="element-1389" data-mp="32xo4hhfloxu1" />}
            {activeSection === 'navigation' && <NavigationSection data-id="element-1390" data-mp="330aju38qghmt" />}
            {activeSection === 'empty-states' && <EmptyStatesSection data-id="element-1391" data-mp="2y5wmhvaig0fi" />}
            {activeSection === 'icons' && <IconsSection data-id="element-1392" data-mp="2t8whlnped3pz" />}
            {activeSection === 'spacing' && <SpacingSection data-id="element-1393" data-mp="2t6a6xf77m83j" />}
            {activeSection === 'grid' && <GridSection data-id="element-1394" data-mp="3qok3ayih4tas" />}
            {activeSection === 'theme' && <ThemeSection data-id="element-1395" data-mp="3lx1z81l5mcll" />}
            {activeSection === 'accessibility' && <AccessibilitySection data-id="element-1396" data-mp="3lvqyybqx3mz8" />}
          </div>
        </div>
      </div>
    </div>;
}
// --- Sections ---
function TypographySection() {
  return <div className="p-8" data-id="element-1397" data-mp="1o1d336lt615l">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1398" data-mp="1nztp5mnmk26z">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1399" data-mp="1aa8b8qoomd5t">Typography</h2>
        <p className="text-slate-500 mt-1" data-id="element-1400" data-mp="1a7vsibb3nyrp">
          Inter is the primary typeface for all UI elements, providing clean,
          highly legible text optimized for data-dense interfaces.
        </p>
      </div>

      <div className="space-y-10" data-id="element-1401" data-mp="15adkqo8tjpla">
        <div data-id="element-1402" data-mp="15boq2shwx5ei">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1403" data-mp="15c6qm1bly5s8">
            Headings
          </h3>
          <div className="space-y-6" data-id="element-1404" data-mp="rd75h3nb090t">
            <div className="flex items-baseline border-b border-slate-100 pb-4" data-id="element-1405" data-mp="w95nm9i1guss">
              <div className="w-32 text-sm text-slate-400 font-mono" data-id="element-1406" data-mp="2lp53e9g5xihe">
                text-3xl
              </div>
              <h1 className="text-3xl font-bold text-slate-900" data-id="element-1407" data-mp="2qo87nrobjkob">Page Title</h1>
            </div>
            <div className="flex items-baseline border-b border-slate-100 pb-4" data-id="element-1408" data-mp="30r3rss3qzcso">
              <div className="w-32 text-sm text-slate-400 font-mono" data-id="element-1409" data-mp="2vt28c62etbgo">
                text-2xl
              </div>
              <h2 className="text-2xl font-semibold text-slate-900" data-id="element-1410" data-mp="39kqgxzdg9hsw">
                Section Heading
              </h2>
            </div>
            <div className="flex items-baseline border-b border-slate-100 pb-4" data-id="element-1411" data-mp="34rxift8kr5p9">
              <div className="w-32 text-sm text-slate-400 font-mono" data-id="element-1412" data-mp="3jh3cl2sk25qa">
                text-xl
              </div>
              <h3 className="text-xl font-semibold text-slate-900" data-id="element-1413" data-mp="3eis1koqixp6h">
                Card Title
              </h3>
            </div>
            <div className="flex items-baseline border-b border-slate-100 pb-4" data-id="element-1414" data-mp="3sgqf5rx0thkx">
              <div className="w-32 text-sm text-slate-400 font-mono" data-id="element-1415" data-mp="3ofd8jfm3tacy">
                text-lg
              </div>
              <h4 className="text-lg font-semibold text-slate-900" data-id="element-1416" data-mp="1y6v4iydxjn5d">
                Subsection Title
              </h4>
            </div>
          </div>
        </div>

        <div data-id="element-1417" data-mp="2invcqvjgwsec">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1418" data-mp="2io4vpz63hzaj">
            Body & UI Text
          </h3>
          <div className="space-y-6" data-id="element-1419" data-mp="2seg9edfkzmum">
            <div className="flex items-start border-b border-slate-100 pb-4" data-id="element-1420" data-mp="2sfhl8k4f8dde">
              <div className="w-32 text-sm text-slate-400 font-mono pt-1" data-id="element-1421" data-mp="2wg1gtj9cj8xf">
                text-base
              </div>
              <p className="text-base text-slate-700 max-w-2xl" data-id="element-1422" data-mp="1zqu4n1umac66">
                The quick brown fox jumps over the lazy dog. This is standard
                body text used for longer paragraphs, descriptions, and general
                reading content.
              </p>
            </div>
            <div className="flex items-start border-b border-slate-100 pb-4" data-id="element-1423" data-mp="2dqcz4rui9p00">
              <div className="w-32 text-sm text-slate-400 font-mono pt-1" data-id="element-1424" data-mp="3k5vu21xkuzi7">
                text-sm
              </div>
              <p className="text-sm text-slate-600 max-w-2xl" data-id="element-1425" data-mp="3p3fke07jv2qi">
                The quick brown fox jumps over the lazy dog. This is the primary
                UI text size, used for list items, form inputs, buttons, and
                dense data displays.
              </p>
            </div>
            <div className="flex items-start border-b border-slate-100 pb-4" data-id="element-1426" data-mp="3phj2fzo7eu68">
              <div className="w-32 text-sm text-slate-400 font-mono pt-1" data-id="element-1427" data-mp="3lhipykko0t1t">
                text-xs
              </div>
              <p className="text-xs text-slate-500 max-w-2xl" data-id="element-1428" data-mp="3gixnsflurzaq">
                The quick brown fox jumps over the lazy dog. Used for metadata,
                timestamps, small badges, and secondary helper text.
              </p>
            </div>
          </div>
        </div>

        <div data-id="element-1429" data-mp="32iellp42jngh">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1430" data-mp="32iwp96t293yf">
            Font Weights
          </h3>
          <div className="flex gap-8" data-id="element-1431" data-mp="14rtr3wmplqia">
            <div data-id="element-1432" data-mp="10qrwxu7ng4n6">
              <div className="text-4xl font-normal text-slate-900 mb-2" data-id="element-1433" data-mp="10q8dua69ja7k">Aa</div>
              <div className="text-sm font-mono text-slate-500" data-id="element-1434" data-mp="1cdlteky3k8yc">
                font-normal (400)
              </div>
            </div>
            <div data-id="element-1435" data-mp="18aywi5mkjxua">
              <div className="text-4xl font-medium text-slate-900 mb-2" data-id="element-1436" data-mp="18bgx1eg9ky80">Aa</div>
              <div className="text-sm font-mono text-slate-500" data-id="element-1437" data-mp="1maqfylvuu6fo">
                font-medium (500)
              </div>
            </div>
            <div data-id="element-1438" data-mp="1halvkkrtsrk6">
              <div className="text-4xl font-semibold text-slate-900 mb-2" data-id="element-1439" data-mp="tn428kswxlf3">
                Aa
              </div>
              <div className="text-sm font-mono text-slate-500" data-id="element-1440" data-mp="ooaul90b4omu">
                font-semibold (600)
              </div>
            </div>
            <div data-id="element-1441" data-mp="13dp5iljj7e4c">
              <div className="text-4xl font-bold text-slate-900 mb-2" data-id="element-1442" data-mp="13e8idnubrcbi">Aa</div>
              <div className="text-sm font-mono text-slate-500" data-id="element-1443" data-mp="ylfcgwn4dxxu">
                font-bold (700)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function ColorsSection() {
  const ColorSwatch = ({
    name,
    hex,
    bgClass,
    textClass = 'text-slate-900'
  }: {
    name: string;
    hex: string;
    bgClass: string;
    textClass?: string;
  }) => <div className="flex flex-col" data-id="element-1444" data-mp="2mcddto3t4er5">
      <div className={`h-24 rounded-lg border border-slate-200 shadow-sm mb-3 ${bgClass}`} data-id="element-1445" data-mp="2mdg5vzcv8n8n"></div>
      <div className="font-medium text-sm text-slate-900" data-id="element-1446" data-mp="2cls9dk1gcg0b">{name}</div>
      <div className="text-xs text-slate-500 font-mono uppercase" data-id="element-1447" data-mp="2cm21bd624x8n">{hex}</div>
    </div>;
  return <div className="p-8" data-id="element-1448" data-mp="309a33l4a1o5x">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1449" data-mp="35ahdlrxt11ga">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1450" data-mp="359od38pfv86l">Colors</h2>
        <p className="text-slate-500 mt-1" data-id="element-1451" data-mp="2r9n1ay7mygcc">
          The color system relies heavily on Slate for structure, with semantic
          colors for status and Indigo for AI/accents.
        </p>
      </div>

      <div className="space-y-10" data-id="element-1452" data-mp="1fuhylu7i8hsq">
        <div data-id="element-1453" data-mp="1fxm9fafh6bsd">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1454" data-mp="1kumdjfsrl4gu">
            Primary & Neutrals (Slate)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1455" data-mp="3miqobb8ac7mp">
            <ColorSwatch name="Slate 900" hex="#0f172a" bgClass="bg-slate-900" data-id="element-1456" data-mp="3hkywsibjynj1" />
            <ColorSwatch name="Slate 700" hex="#334155" bgClass="bg-slate-700" data-id="element-1457" data-mp="3csnu57pfgnms" />
            <ColorSwatch name="Slate 500" hex="#64748b" bgClass="bg-slate-500" data-id="element-1458" data-mp="38qamuev18hnw" />
            <ColorSwatch name="Slate 200" hex="#e2e8f0" bgClass="bg-slate-200" data-id="element-1459" data-mp="2yto4pxu3c4pk" />
            <ColorSwatch name="Slate 50" hex="#f8fafc" bgClass="bg-slate-50" data-id="element-1460" data-mp="2tvmnzjk50hh9" />
          </div>
        </div>

        <div data-id="element-1461" data-mp="2pv3wjqm5pdfl">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1462" data-mp="2pvm078b5etxj">
            Semantic: Success (Emerald)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1463" data-mp="o1wei9f5shq0">
            <ColorSwatch name="Emerald 600" hex="#059669" bgClass="bg-emerald-600" data-id="element-1464" data-mp="25zsrpl41qimn" />
            <ColorSwatch name="Emerald 500" hex="#10b981" bgClass="bg-emerald-500" data-id="element-1465" data-mp="2axu981ltq9w0" />
            <ColorSwatch name="Emerald 100" hex="#d1fae5" bgClass="bg-emerald-100" data-id="element-1466" data-mp="2frp31xv5858t" />
            <ColorSwatch name="Emerald 50" hex="#ecfdf5" bgClass="bg-emerald-50" data-id="element-1467" data-mp="1n1ghdw1ef2ph" />
          </div>
        </div>

        <div data-id="element-1468" data-mp="1s2496jxgbnkr">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1469" data-mp="1w1le9c8yuipe">
            Semantic: Warning (Amber)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1470" data-mp="211hhh99ua63w">
            <ColorSwatch name="Amber 600" hex="#d97706" bgClass="bg-amber-600" data-id="element-1471" data-mp="37h0huyvok8th" />
            <ColorSwatch name="Amber 500" hex="#f59e0b" bgClass="bg-amber-500" data-id="element-1472" data-mp="3cejwhzwainly" />
            <ColorSwatch name="Amber 100" hex="#fef3c7" bgClass="bg-amber-100" data-id="element-1473" data-mp="3b0iwkdbjh78q" />
            <ColorSwatch name="Amber 50" hex="#fffbeb" bgClass="bg-amber-50" data-id="element-1474" data-mp="3jzvxuipiepid" />
          </div>
        </div>

        <div data-id="element-1475" data-mp="3fxj12jsqq1xq">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1476" data-mp="3fwzjj4705fk8">
            Semantic: Danger (Rose)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1477" data-mp="3tv7dr4cnm2rp">
            <ColorSwatch name="Rose 600" hex="#e11d48" bgClass="bg-rose-600" data-id="element-1478" data-mp="3oxxpvt4wxz5z" />
            <ColorSwatch name="Rose 500" hex="#f43f5e" bgClass="bg-rose-500" data-id="element-1479" data-mp="7gxo6rwzmopc" />
            <ColorSwatch name="Rose 100" hex="#ffe4e6" bgClass="bg-rose-100" data-id="element-1480" data-mp="2icp4vth2b2h" />
            <ColorSwatch name="Rose 50" hex="#fff1f2" bgClass="bg-rose-50" data-id="element-1481" data-mp="gingdzyp1ro6" />
          </div>
        </div>

        <div data-id="element-1482" data-mp="bjudf1h19j29">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-6" data-id="element-1483" data-mp="bk2oqoxicgbc">
            Accent & AI (Indigo)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1484" data-mp="v5vw9e2y6l6h">
            <ColorSwatch name="Indigo 600" hex="#4f46e5" bgClass="bg-indigo-600" data-id="element-1485" data-mp="104pdnhlcuwbx" />
            <ColorSwatch name="Indigo 500" hex="#6366f1" bgClass="bg-indigo-500" data-id="element-1486" data-mp="m4ek26snv3n2" />
            <ColorSwatch name="Indigo 100" hex="#e0e7ff" bgClass="bg-indigo-100" data-id="element-1487" data-mp="cf4r6opztgxw" />
            <ColorSwatch name="Indigo 50" hex="#eef2ff" bgClass="bg-indigo-50" data-id="element-1488" data-mp="h6mpf50mt3f6" />
          </div>
        </div>
      </div>
    </div>;
}
function ButtonsSection() {
  return <div className="p-8" data-id="element-1489" data-mp="7g1o39tkpksk">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1490" data-mp="3plej6i4l43yo">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1491" data-mp="3plwnm21ehoho">Buttons</h2>
        <p className="text-slate-500 mt-1" data-id="element-1492" data-mp="3ujg3159u47b7">
          Interactive elements for actions, using a consistent rounded-md style
          with clear hover states.
        </p>
      </div>

      <div className="space-y-10" data-id="element-1493" data-mp="1vd0zm49n1xom">
        <div data-id="element-1494" data-mp="1vbzpq34czh8h">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1495" data-mp="1vc7ypjxd22ee">
            Primary
          </h3>
          <div className="flex flex-wrap gap-4 items-center p-6 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1496" data-mp="1mcutj3g9rzzh">
            <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-1497" data-mp="2j3n9m137ds19">
              Primary Action
            </button>
            <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-1498" data-mp="2a2fe1qs2n5av">
              <Plus className="h-4 w-4 mr-2" data-id="element-1499" data-mp="252ub759s6csv" />
              With Icon
            </button>
            <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-1500" data-mp="yo1pskteonub">
              Trailing Icon
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-1501" data-mp="35cbxobxyu5pn" />
            </button>
            <button disabled className="bg-slate-300 text-white px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed" data-id="element-1502" data-mp="3aadojj1mv9bo">
              Disabled
            </button>
          </div>
        </div>

        <div data-id="element-1503" data-mp="3j3qrldtevrbh">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1504" data-mp="3j4idfwx7iaok">
            Secondary (Outline)
          </h3>
          <div className="flex flex-wrap gap-4 items-center p-6 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1505" data-mp="3t14x4idsqvp0">
            <button className="border border-slate-200 text-slate-700 bg-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-1506" data-mp="1uaifhq65spg">
              Secondary Action
            </button>
            <button className="border border-slate-200 text-slate-700 bg-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center" data-id="element-1507" data-mp="23myorzy40w9g">
              <Download className="h-4 w-4 mr-2" data-id="element-1508" data-mp="1or9yq8bh7prf" />
              Export
            </button>
            <button disabled className="border border-slate-200 text-slate-400 bg-slate-50 px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed" data-id="element-1509" data-mp="1jsgjaacmpoom">
              Disabled
            </button>
          </div>
        </div>

        <div data-id="element-1510" data-mp="1tj1qgm6dc1j5">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1511" data-mp="1tkmahhv2422r">
            Ghost & Destructive
          </h3>
          <div className="flex flex-wrap gap-4 items-center p-6 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1512" data-mp="10ucnehlr5m4r">
            <button className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-4 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-1513" data-mp="1eueztobajncm">
              Ghost Button
            </button>
            <button className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center" data-id="element-1514" data-mp="2q7hhynt1gkyw">
              <Trash2 className="h-4 w-4 mr-2" data-id="element-1515" data-mp="2tqgceevryiwy" />
              Delete Item
            </button>
            <button className="bg-rose-600 text-white hover:bg-rose-700 px-4 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-1516" data-mp="2xs1vihywjtfl">
              Destructive Primary
            </button>
          </div>
        </div>

        <div data-id="element-1517" data-mp="2or3sa1wafpy2">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1518" data-mp="2oqk7mdf96ngc">
            Icon Buttons
          </h3>
          <div className="flex flex-wrap gap-4 items-center p-6 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1519" data-mp="3chg5nsaayew1">
            <button className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors border border-transparent" data-id="element-1520" data-mp="3hi3uc7b26jn3">
              <MoreVertical className="h-5 w-5" data-id="element-1521" data-mp="37pfzvj7jwrrj" />
            </button>
            <button className="p-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors" data-id="element-1522" data-mp="3vfsflg91fsso">
              <Edit2 className="h-4 w-4" data-id="element-1523" data-mp="41e95cojxhzk" />
            </button>
            <button className="p-2 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-md transition-colors" data-id="element-1524" data-mp="1vm7d71e81ohe">
              <Trash2 className="h-4 w-4" data-id="element-1525" data-mp="1znkklfwypzqf" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function CardsSection() {
  return <div className="p-8" data-id="element-1526" data-mp="1lvw7b9az957v">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1527" data-mp="1lul3xalg1zha">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1528" data-mp="1qu7h5k19vfq3">Cards</h2>
        <p className="text-slate-500 mt-1" data-id="element-1529" data-mp="1bxq5sst3213j">
          Containers for grouping related content. Standardized with rounded-xl,
          border-slate-200, and shadow-sm.
        </p>
      </div>

      <div className="space-y-8" data-id="element-1530" data-mp="12y34s849mxb0">
        <div data-id="element-1531" data-mp="12yuz7fm4mozp">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1532" data-mp="12ze7e4lz5z0j">
            Standard Card
          </h3>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 max-w-md" data-id="element-1533" data-mp="2xbkthygeq07m">
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1534" data-mp="32anosr6l4s2e">
              Card Title
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-1535" data-mp="3cecif0ccxnsl">
              This is a standard card used for displaying content, forms, or
              settings. It has consistent padding and a subtle shadow.
            </p>
            <button className="border border-slate-200 text-slate-700 bg-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-1536" data-mp="3l7xzwrfwpj5p">
              Action
            </button>
          </div>
        </div>

        <div data-id="element-1537" data-mp="3v393qeps15yo">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1538" data-mp="3v76pl83qobm9">
            Card with Header & Footer
          </h3>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden max-w-md" data-id="element-1539" data-mp="80nuri0nh4x4">
            <div className="p-5 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-1540" data-mp="3zu80ryy1w5t">
              <h3 className="font-medium text-slate-900" data-id="element-1541" data-mp="29sslwrabry6s">Structured Card</h3>
              <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full font-medium" data-id="element-1542" data-mp="25qnpxlwempgz">
                New
              </span>
            </div>
            <div className="p-5" data-id="element-1543" data-mp="8rw1eeau3pqz">
              <p className="text-sm text-slate-600" data-id="element-1544" data-mp="do313pcn050h">
                Content area with no background color. Headers and footers often
                use slate-50 to distinguish them from the main content.
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end" data-id="element-1545" data-mp="no3l7dibyp5q">
              <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-1546" data-mp="rnujs3e3m9kt">
                Save Changes
              </button>
            </div>
          </div>
        </div>

        <div data-id="element-1547" data-mp="11gjidxojvsyq">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1548" data-mp="16d0hfmxafrql">
            Interactive / Hover Card
          </h3>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group max-w-md" data-id="element-1549" data-mp="1bdo8g8lio8kt">
            <div className="flex justify-between items-start mb-3" data-id="element-1550" data-mp="2zw94zyx4vrbe">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600 group-hover:bg-slate-900 group-hover:text-white transition-colors" data-id="element-1551" data-mp="2vuf6nxaeuzee">
                <FileText className="h-5 w-5" data-id="element-1552" data-mp="2qwdorfoq164i" />
              </div>
              <MoreVertical className="h-5 w-5 text-slate-400" data-id="element-1553" data-mp="2lwhj7c0dl6mu" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-1" data-id="element-1554" data-mp="2lyklauesyq7x">
              Clickable Item
            </h3>
            <p className="text-sm text-slate-500" data-id="element-1555" data-mp="3ekwv3po7n8ty">
              Hover over this card to see the transition effects.
            </p>
          </div>
        </div>
      </div>
    </div>;
}
function TablesSection() {
  return <div className="p-8" data-id="element-1556" data-mp="34vvi88cyp7k7">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1557" data-mp="34uak68rfv6hl">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1558" data-mp="65lkz0a99ryj">Tables</h2>
        <p className="text-slate-500 mt-1" data-id="element-1559" data-mp="66wpj2biz3qp">
          Used for displaying dense datasets. Features uppercase slate-500
          headers and divide-y rows.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1560" data-mp="3i0gc3ag7ipmd">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-1561" data-mp="3m4o1zayeibm6">
          <h3 className="font-medium text-slate-900" data-id="element-1562" data-mp="385ek86ujr9g3">Data Table Example</h3>
          <div className="relative" data-id="element-1563" data-mp="3d3g4gv3plet5">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1564" data-mp="3d4hse05tpvsa" />
            <input type="text" placeholder="Search..." className="pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-1565" data-mp="4nmyvbax1nqv" />
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1566" data-mp="3w0hb0kcwvmz7">
          <table className="w-full text-sm text-left" data-id="element-1567" data-mp="3vzfxm98faoeb">
            <thead className="text-xs text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200" data-id="element-1568" data-mp="nko6z4zro3z1">
              <tr data-id="element-1569" data-mp="se0hkb7m2a8s">
                <th className="px-6 py-3 font-medium" data-id="element-1570" data-mp="seimdw8c9wsb">Name</th>
                <th className="px-6 py-3 font-medium" data-id="element-1571" data-mp="2d1t7rw7htcj9">Role</th>
                <th className="px-6 py-3 font-medium" data-id="element-1572" data-mp="28400t0yd8ehx">Status</th>
                <th className="px-6 py-3 font-medium text-right" data-id="element-1573" data-mp="283hxxlh77211">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1574" data-mp="2lwzfj0qbq4of">
              {[{
              name: 'Jane Doe',
              role: 'Director',
              status: 'Active',
              statusClass: 'bg-emerald-100 text-emerald-800'
            }, {
              name: 'John Smith',
              role: 'Manager',
              status: 'Pending',
              statusClass: 'bg-amber-100 text-amber-800'
            }, {
              name: 'Alice Johnson',
              role: 'Analyst',
              status: 'Inactive',
              statusClass: 'bg-slate-100 text-slate-800'
            }].map((row, i) => <tr key={i} className="hover:bg-slate-50 transition-colors" data-id="element-1575" data-mp="27849molry31y" data-mpi={i}>
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1576" data-mp="2c65tvcuxs8f0">
                    {row.name}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-1577" data-mp="2g92grfrieqor">{row.role}</td>
                  <td className="px-6 py-4" data-id="element-1578" data-mp="2l6ub0gfmmow4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.statusClass}`} data-id="element-1579" data-mp="2l6khiivdhzlo">
                      {row.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-1580" data-mp="1xgzv1q00gk7n">
                    <button className="text-slate-400 hover:text-slate-600" data-id="element-1581" data-mp="1xff0w1hlz3ab">
                      <MoreVertical className="h-4 w-4" data-id="element-1582" data-mp="229t1qjf1i2md" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function FormsSection() {
  return <div className="p-8" data-id="element-1583" data-mp="12865tmqne4dj">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1584" data-mp="127d4j1agk72s">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1585" data-mp="x9bj49pk7vo5">Forms</h2>
        <p className="text-slate-500 mt-1" data-id="element-1586" data-mp="x78m35pzp29x">
          Input elements for data collection. Clean borders, clear focus rings,
          and consistent spacing.
        </p>
      </div>

      <div className="max-w-xl space-y-8" data-id="element-1587" data-mp="o83mdw2oz2w6">
        <div className="space-y-4" data-id="element-1588" data-mp="j9k363fhg57i">
          <div data-id="element-1589" data-mp="jalm0u2qpvzs">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1590" data-mp="jb4tfgurl1zk">
              Standard Input
            </label>
            <input type="text" placeholder="Enter text here..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all" data-id="element-1591" data-mp="eh0nn0x8j002" />
          </div>

          <div data-id="element-1592" data-mp="l57v3y4u50w">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1593" data-mp="3rpdg68aluyjt">
              Input with Icon
            </label>
            <div className="relative" data-id="element-1594" data-mp="1jge341bkyqil">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1595" data-mp="1jhgpbvxyk4s6" />
              <input type="text" placeholder="Search..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all" data-id="element-1596" data-mp="1oefh47u5fzn7" />
            </div>
          </div>

          <div data-id="element-1597" data-mp="10ov0hvlgxeh3">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1598" data-mp="10ocsy2tcvdtv">
              Select Dropdown
            </label>
            <div className="relative" data-id="element-1599" data-mp="qsi76ytu52mv">
              <select className="w-full pl-3 pr-9 py-2 border border-slate-200 rounded-md text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all appearance-none bg-white" data-id="element-1600" data-mp="qtkyv8yzf93u">
                <option data-id="element-1601" data-mp="hs3hzodr33e3">Option 1</option>
                <option data-id="element-1602" data-mp="mq4zi4vj2ung">Option 2</option>
                <option data-id="element-1603" data-mp="mpvb2lq4svjv">Option 3</option>
              </select>
              <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-1604" data-mp="3438h4zgmm85" />
            </div>
          </div>

          <div data-id="element-1605" data-mp="3uch3fw8qu09h">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1606" data-mp="3ud0lre2b2qo1">
              Textarea
            </label>
            <textarea rows={4} placeholder="Enter detailed description..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-100 outline-none transition-all resize-y" data-id="element-1607" data-mp="83pm3fj7a4hh" />
          </div>

          <div data-id="element-1608" data-mp="vt0mgvwn1wv3">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1609" data-mp="vtjwzrjyliz3">
              Input with Error
            </label>
            <input type="text" defaultValue="Invalid value" className="w-full px-3 py-2 border border-rose-300 text-rose-900 rounded-md text-sm focus:ring-2 focus:ring-rose-100 outline-none transition-all" data-id="element-1610" data-mp="qupeyrttfyg1" />
            <p className="mt-1 text-xs text-rose-500" data-id="element-1611" data-mp="227bqsko760f2">
              This field is required and must be valid.
            </p>
          </div>
        </div>

        <div className="pt-6 border-t border-slate-200 space-y-4" data-id="element-1612" data-mp="1x5w30kumxfsz">
          <h3 className="text-sm font-medium text-slate-900" data-id="element-1613" data-mp="1s7kss90fh3a8">
            Toggles & Checkboxes
          </h3>

          <label className="flex items-center space-x-3 cursor-pointer" data-id="element-1614" data-mp="1n99oedswjkze">
            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" defaultChecked data-id="element-1615" data-mp="1j996ukaiato4" />
            <span className="text-sm text-slate-700" data-id="element-1616" data-mp="19hlaq6308ogb">Standard Checkbox</span>
          </label>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg" data-id="element-1617" data-mp="14jthn8qmiwaj">
            <div data-id="element-1618" data-mp="zkglfxdv4cva">
              <div className="text-sm font-medium text-slate-900" data-id="element-1619" data-mp="zix4e4kdtxsg">
                Enable Feature
              </div>
              <div className="text-xs text-slate-500" data-id="element-1620" data-mp="vj5229ll0nvj">
                Turn this on to activate the feature.
              </div>
            </div>
            <ToggleRight className="h-6 w-6 text-emerald-500 cursor-pointer" data-id="element-1621" data-mp="3b7adbjdggwgc" />
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg" data-id="element-1622" data-mp="3g6wqjstaacp5">
            <div data-id="element-1623" data-mp="3l5hpzq0poscj">
              <div className="text-sm font-medium text-slate-900" data-id="element-1624" data-mp="3l5zybl0nex0t">
                Disabled Feature
              </div>
              <div className="text-xs text-slate-500" data-id="element-1625" data-mp="2njal0f0hwxzn">
                This feature is currently off.
              </div>
            </div>
            <ToggleLeft className="h-6 w-6 text-slate-300 cursor-pointer" data-id="element-1626" data-mp="2sepwj0b16hsw" />
          </div>
        </div>
      </div>
    </div>;
}
function AlertsSection() {
  return <div className="p-8" data-id="element-1627" data-mp="328gi9xfrwe4w">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1628" data-mp="328oz20f0o3ld">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1629" data-mp="24qowygvtybpm">
          Alerts & Banners
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1630" data-mp="29p9u27fscf9u">
          Used to communicate important information, state changes, or system
          status.
        </p>
      </div>

      <div className="space-y-4 max-w-2xl" data-id="element-1631" data-mp="1xotfzlan8r57">
        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start" data-id="element-1632" data-mp="1xmh15h06mwwd">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 shrink-0" data-id="element-1633" data-mp="2bhst5gmye99l" />
          <div data-id="element-1634" data-mp="26hwt1shdn1zb">
            <h4 className="text-sm font-medium text-blue-900" data-id="element-1635" data-mp="26hdgyseer7t7">Information</h4>
            <p className="text-sm text-blue-800 mt-1" data-id="element-1636" data-mp="1irsoniwd6y79">
              A new software update is available. Please refresh your browser to
              apply the changes.
            </p>
          </div>
        </div>

        {/* Success */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start" data-id="element-1637" data-mp="1smud0citfw8s">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 mr-3 shrink-0" data-id="element-1638" data-mp="1nsgmooj0gfaz" />
          <div data-id="element-1639" data-mp="342j2bajjl7cr">
            <h4 className="text-sm font-medium text-emerald-900" data-id="element-1640" data-mp="3466v27h5xpqf">Success</h4>
            <p className="text-sm text-emerald-800 mt-1" data-id="element-1641" data-mp="2z510s57utues">
              Your assessment has been successfully published and invitations
              have been sent.
            </p>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start" data-id="element-1642" data-mp="bx38p321k3d2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 shrink-0" data-id="element-1643" data-mp="gvwq36kg8eii" />
          <div data-id="element-1644" data-mp="gxrb0kvq06ml">
            <h4 className="text-sm font-medium text-amber-900" data-id="element-1645" data-mp="14lhbrx7zlpvh">Warning</h4>
            <p className="text-sm text-amber-800 mt-1" data-id="element-1646" data-mp="14nll4uonnh35">
              You are about to delete a template that is currently in use by 3
              active assessments.
            </p>
          </div>
        </div>

        {/* Error */}
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start" data-id="element-1647" data-mp="zu8xonvppgbz">
          <XCircle className="h-5 w-5 text-rose-600 mt-0.5 mr-3 shrink-0" data-id="element-1648" data-mp="1nkmsgm5olta9" />
          <div data-id="element-1649" data-mp="1niiflept1jxu">
            <h4 className="text-sm font-medium text-rose-900" data-id="element-1650" data-mp="1satf4ggmv3pv">Error</h4>
            <p className="text-sm text-rose-800 mt-1" data-id="element-1651" data-mp="1sd67zonxiyhp">
              Failed to save changes. Please check your network connection and
              try again.
            </p>
          </div>
        </div>

        {/* AI Insight */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start mt-8" data-id="element-1652" data-mp="3qtoo0x36d60w">
          <Sparkles className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 shrink-0" data-id="element-1653" data-mp="3lv55l6nsicda" />
          <div data-id="element-1654" data-mp="3lx7zi1s2cpna">
            <h4 className="text-sm font-medium text-indigo-900" data-id="element-1655" data-mp="mfi7ms40kr29">AI Insight</h4>
            <p className="text-sm text-indigo-800 mt-1" data-id="element-1656" data-mp="mhkvb5hp283t">
              Based on recent data, we recommend focusing on Communication
              skills in the next quarter.
            </p>
          </div>
        </div>
      </div>
    </div>;
}
function ChartsSection() {
  const data = [{
    name: 'Jan',
    value: 400
  }, {
    name: 'Feb',
    value: 300
  }, {
    name: 'Mar',
    value: 600
  }, {
    name: 'Apr',
    value: 800
  }, {
    name: 'May',
    value: 500
  }];
  return <div className="p-8" data-id="element-1657" data-mp="18pgvbwla2kze">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1658" data-mp="18q052q0ry32c">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1659" data-mp="1dobbsrvrvxgc">Charts</h2>
        <p className="text-slate-500 mt-1" data-id="element-1660" data-mp="1iml6l1dl4w1f">
          Data visualization using Recharts. Styled to match the clean,
          slate-based aesthetic.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1661" data-mp="1mlu48hbqxe29">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-1662" data-mp="1rm8abttsedxn">
          <h3 className="text-sm font-medium text-slate-900 mb-6" data-id="element-1663" data-mp="1wlcm8s83ipro">Bar Chart</h3>
          <div className="h-64 w-full" data-id="element-1664" data-mp="21d2umsnh33gg">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1665" data-mp="21dcq2vrae2tj">
              <BarChart data={data} data-id="element-1666" data-mp="26cfi9fm64ek3">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-1667" data-mp="26be8degw1y3y" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} dy={10} data-id="element-1668" data-mp="2fc2lm6ygjufs" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-1669" data-mp="ozw32f2zwxt2" />
                <Tooltip cursor={{
                fill: '#f8fafc'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }} data-id="element-1670" data-mp="yxajieox5mld" />
                <Bar dataKey="value" fill="#0f172a" radius={[4, 4, 0, 0]} data-id="element-1671" data-mp="12xufhexrak5x" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-1672" data-mp="34sd6rbgyxfu0">
          <h3 className="text-sm font-medium text-slate-900 mb-6" data-id="element-1673" data-mp="1k8znqlg6ad09">
            Line Chart
          </h3>
          <div className="h-64 w-full" data-id="element-1674" data-mp="1p8m1qx3trxa4">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1675" data-mp="1p7ks8x2gjiui">
              <LineChart data={data} data-id="element-1676" data-mp="1bfwekpck8mbf">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-1677" data-mp="1bev2qinpzvsn" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} dy={10} data-id="element-1678" data-mp="11hqdub2hpm85" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-1679" data-mp="wiwyed3n7l5c" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }} data-id="element-1680" data-mp="2qx69qorelbg3" />
                <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={3} dot={{
                r: 4,
                fill: '#4f46e5',
                strokeWidth: 0
              }} activeDot={{
                r: 6
              }} data-id="element-1681" data-mp="14y9rote4r1fv" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}
function NavigationSection() {
  return <div className="p-8" data-id="element-1682" data-mp="h25hno4liv3y">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1683" data-mp="h2ypytj7dso7">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1684" data-mp="2gcev9lafl9ao">Navigation</h2>
        <p className="text-slate-500 mt-1" data-id="element-1685" data-mp="2bedh9erv4062">
          Patterns for moving through the application.
        </p>
      </div>

      <div className="space-y-10" data-id="element-1686" data-mp="2juugql5hvt6u">
        <div data-id="element-1687" data-mp="2jtt62hse58pn">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1688" data-mp="2nwzarkzuuru0">
            Tabs
          </h3>
          <div className="border-b border-slate-200" data-id="element-1689" data-mp="2sno2n1fg5qto">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-1690" data-mp="2sqa9eyuik2au">
              <button className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-slate-900 text-slate-900" data-id="element-1691" data-mp="2xmiwcyeyczsk">
                Active Tab
              </button>
              <button className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300" data-id="element-1692" data-mp="24zemaf105v9x">
                Inactive Tab
              </button>
              <button className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 flex items-center" data-id="element-1693" data-mp="2ey4467swffq6">
                With Badge
                <span className="ml-2 bg-slate-100 text-slate-600 py-0.5 px-2 rounded-full text-xs" data-id="element-1694" data-mp="3qdsrx1dz8is1">
                  3
                </span>
              </button>
            </nav>
          </div>
        </div>

        <div data-id="element-1695" data-mp="3rrro1j687679">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1696" data-mp="jx0vfsvp068">
            Sidebar Items
          </h3>
          <div className="w-64 bg-white border border-slate-200 rounded-xl p-4 space-y-1" data-id="element-1697" data-mp="5jrwfwnlmdxm">
            <button className="w-full flex items-center px-3 py-2 rounded-md text-sm font-medium bg-slate-100 text-slate-900" data-id="element-1698" data-mp="ags0y34sv8mm">
              <LayoutGrid className="mr-3 h-4 w-4 text-slate-900" data-id="element-1699" data-mp="ehlogvebylez" />
              Active Item
            </button>
            <button className="w-full flex items-center px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900" data-id="element-1700" data-mp="jc7z2wcl89y0">
              <Users className="mr-3 h-4 w-4 text-slate-400" data-id="element-1701" data-mp="t9ds4a4rib0v" />
              Inactive Item
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function EmptyStatesSection() {
  return <div className="p-8" data-id="element-1702" data-mp="2iq7xm48rjevg">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1703" data-mp="2iqrdlfeurt6u">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1704" data-mp="2dpbuvu05dyrm">Empty States</h2>
        <p className="text-slate-500 mt-1" data-id="element-1705" data-mp="28r0ojt92a6e5">
          Used when a list, table, or page has no content to display.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center max-w-2xl mx-auto" data-id="element-1706" data-mp="23th4udtlh1et">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1707" data-mp="31f66674tjf9r">
          <FileText className="h-8 w-8 text-slate-400" data-id="element-1708" data-mp="2wixw385hac9g" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-1709" data-mp="2rotocmoe207b">
          No reports generated yet
        </h3>
        <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto" data-id="element-1710" data-mp="2mqicy7ig3hmz">
          Get started by creating your first assessment cycle. Once respondents
          complete it, reports will appear here.
        </p>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center mx-auto" data-id="element-1711" data-mp="3fee5r14w8peh">
          <Plus className="h-4 w-4 mr-2" data-id="element-1712" data-mp="1ihjdzngf1rbj" />
          Create Assessment
        </button>
      </div>
    </div>;
}
function IconsSection() {
  const icons = [{
    name: 'Layout',
    icon: LayoutGrid
  }, {
    name: 'Users',
    icon: Users
  }, {
    name: 'Settings',
    icon: Settings
  }, {
    name: 'Search',
    icon: Search
  }, {
    name: 'Bell',
    icon: Bell
  }, {
    name: 'Menu',
    icon: Menu
  }, {
    name: 'Plus',
    icon: Plus
  }, {
    name: 'Edit',
    icon: Edit2
  }, {
    name: 'Trash',
    icon: Trash2
  }, {
    name: 'Download',
    icon: Download
  }, {
    name: 'FileText',
    icon: FileText
  }, {
    name: 'Sparkles',
    icon: Sparkles
  }];
  return <div className="p-8" data-id="element-1713" data-mp="2k5u5qj8r4cp0">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1714" data-mp="8j9uhu8bcnwz">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1715" data-mp="8jryxe54q8fz">Icons</h2>
        <p className="text-slate-500 mt-1" data-id="element-1716" data-mp="3kym7nxo2lgv">
          We use Lucide React for all iconography. Icons should generally be
          16px (h-4 w-4) or 20px (h-5 w-5) and use slate-400 or slate-500 unless
          active.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-1717" data-mp="dh1u7af79ebq">
        {icons.map((item, i) => {
        const Icon = item.icon;
        return <div key={i} className="flex flex-col items-center justify-center p-6 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-1718" data-mp="m9n564clagq0" data-mpi={i}>
              <Icon className="h-6 w-6 text-slate-700 mb-3" data-id="element-1719" data-mp="wa5qz4nps7ak" />
              <span className="text-xs font-medium text-slate-500" data-id="element-1720" data-mp="32ysil1loscqr">
                {item.name}
              </span>
            </div>;
      })}
      </div>
    </div>;
}
function SpacingSection() {
  return <div className="p-8" data-id="element-1721" data-mp="1p27p5eszs2ue">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1722" data-mp="1p30z0on8z8gr">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1723" data-mp="1u1knavpbcwca">Spacing</h2>
        <p className="text-slate-500 mt-1" data-id="element-1724" data-mp="1z1goyoajgboo">
          We use the standard Tailwind spacing scale (based on 4px increments).
        </p>
      </div>

      <div className="space-y-4 max-w-2xl" data-id="element-1725" data-mp="231qy29tmni7r">
        {[{
        class: 'w-4',
        size: '1rem (16px)',
        label: '4'
      }, {
        class: 'w-6',
        size: '1.5rem (24px)',
        label: '6'
      }, {
        class: 'w-8',
        size: '2rem (32px)',
        label: '8'
      }, {
        class: 'w-12',
        size: '3rem (48px)',
        label: '12'
      }, {
        class: 'w-16',
        size: '4rem (64px)',
        label: '16'
      }, {
        class: 'w-24',
        size: '6rem (96px)',
        label: '24'
      }].map((space, i) => <div key={i} className="flex items-center" data-id="element-1726" data-mp="f59lwkvitznu" data-mpi={i}>
            <div className="w-16 text-sm font-mono text-slate-500" data-id="element-1727" data-mp="f6koihd8d1dd">
              {space.label}
            </div>
            <div className="w-32 text-sm text-slate-400" data-id="element-1728" data-mp="b1ut0tmhhosz">{space.size}</div>
            <div className={`h-8 bg-indigo-100 border border-indigo-200 rounded ${space.class}`} data-id="element-1729" data-mp="63jlipjnvqdx"></div>
          </div>)}
      </div>
    </div>;
}
function GridSection() {
  return <div className="p-8" data-id="element-1730" data-mp="3simhu2yadtyf">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1731" data-mp="3sicoq6hy36oi">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1732" data-mp="3nlw2xiy7x4el">
          Responsive Grid
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1733" data-mp="3jksisk950m7k">
          Standard 12-column grid system using Tailwind's grid utilities.
        </p>
      </div>

      <div className="space-y-8" data-id="element-1734" data-mp="39lbhef0x4uhe">
        <div data-id="element-1735" data-mp="39l1qmp81ujan">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1736" data-mp="39mmlkfya0491">
            12 Columns
          </h3>
          <div className="grid grid-cols-12 gap-4" data-id="element-1737" data-mp="2zzsdqjvmikor">
            {Array.from({
            length: 12
          }).map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded-md border border-slate-200 flex items-center justify-center text-xs text-slate-400" data-id="element-1738" data-mp="2q01hodrezxmk" data-mpi={i}>
                1
              </div>)}
          </div>
        </div>

        <div data-id="element-1739" data-mp="3nnaakttqw8yl">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4" data-id="element-1740" data-mp="3nn0i8zl89ppq">
            Common Layouts
          </h3>
          <div className="grid grid-cols-12 gap-4 mb-4" data-id="element-1741" data-mp="38xm83p9tv49a">
            <div className="col-span-8 h-12 bg-indigo-50 rounded-md border border-indigo-100 flex items-center justify-center text-xs text-indigo-400" data-id="element-1742" data-mp="3dx77u1wtngn2">
              col-span-8 (Main Content)
            </div>
            <div className="col-span-4 h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center text-xs text-slate-400" data-id="element-1743" data-mp="a8x5s7xcl9m0">
              col-span-4 (Sidebar)
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4" data-id="element-1744" data-mp="1q0hggef4egos">
            <div className="h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center text-xs text-slate-400" data-id="element-1745" data-mp="1pxvceorfujbb">
              1/3
            </div>
            <div className="h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center text-xs text-slate-400" data-id="element-1746" data-mp="1zq137v5yeuox">
              1/3
            </div>
            <div className="h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center text-xs text-slate-400" data-id="element-1747" data-mp="171viv9itb7pm">
              1/3
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function ThemeSection() {
  return <div className="p-8" data-id="element-1748" data-mp="2wfs0g25mqvyf">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1749" data-mp="2wf0c9ced40i6">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1750" data-mp="2whmgp35yhzw6">Light Theme</h2>
        <p className="text-slate-500 mt-1" data-id="element-1751" data-mp="2sdwzwzviwmlp">
          Oversight Insights uses a strict Light Theme to maintain a clean,
          document-like enterprise feel.
        </p>
      </div>

      <div className="prose prose-slate max-w-3xl" data-id="element-1752" data-mp="ur466lkt8wcf">
        <p data-id="element-1753" data-mp="zpyz4gchs1a9">
          The application is designed exclusively in a light theme. This
          decision was made to:
        </p>
        <ul data-id="element-1754" data-mp="14hgyx12s3vtn">
          <li data-id="element-1755" data-mp="14hytlt9sm1zg">Ensure maximum readability for dense data tables and reports.</li>
          <li data-id="element-1756" data-mp="18gza354sssja">
            Match the expectation of document-centric workflows (PDF exports,
            printed reports).
          </li>
          <li data-id="element-1757" data-mp="1dj6ria8ed0t0">
            Maintain a crisp, professional aesthetic inspired by tools like
            Linear and Stripe.
          </li>
        </ul>
        <p data-id="element-1758" data-mp="1nfjkf5w4bamp">
          <strong data-id="element-1759" data-mp="1ne8ht9ees8x6">Key Principles:</strong>
        </p>
        <ul data-id="element-1760" data-mp="1ncxaizlr8j1b">
          <li data-id="element-1761" data-mp="3k7tic4yjp285">
            Backgrounds are primarily <code data-id="element-1762" data-mp="3k8v2cyxjgz20">bg-slate-50</code> for the app shell
            and <code data-id="element-1763" data-mp="3p6d1xypo21xc">bg-white</code> for content cards.
          </li>
          <li data-id="element-1764" data-mp="3p8qxs9s67i5y">
            Borders (<code data-id="element-1765" data-mp="26big292mt5j2">border-slate-200</code>) are used extensively to
            separate content areas rather than relying on heavy shadows or
            background fills.
          </li>
          <li data-id="element-1766" data-mp="21iyy6zwwvo8g">
            Text hierarchy relies on <code data-id="element-1767" data-mp="1xif6ibv3xctp">text-slate-900</code> for primary
            content and <code data-id="element-1768" data-mp="1sk3ubuhcaq8b">text-slate-500</code> for secondary/helper text.
          </li>
        </ul>
      </div>
    </div>;
}
function AccessibilitySection() {
  return <div className="p-8" data-id="element-1769" data-mp="2p9uottpmsde4">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1770" data-mp="2p81sp8cn9bkd">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1771" data-mp="2la3zzvjo07c8">
          Accessibility (WCAG 2.1 AA)
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1772" data-mp="2g84zquj81qji">
          Guidelines for ensuring the application is usable by everyone.
        </p>
      </div>

      <div className="space-y-6 max-w-3xl" data-id="element-1773" data-mp="14u9ing8rb7pn">
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-1774" data-mp="14vc7limir02x">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center" data-id="element-1775" data-mp="zv6mymeo2duc">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2" data-id="element-1776" data-mp="3u8y2wsusg9ce" />
            Color Contrast
          </h3>
          <p className="text-sm text-slate-600 mb-4" data-id="element-1777" data-mp="3pamsafwo5ut4">
            All text must meet a minimum contrast ratio of 4.5:1 against its
            background. Our primary text (slate-900 on white) exceeds 15:1.
            Secondary text (slate-500 on white) meets the 4.5:1 requirement.
          </p>
          <div className="flex gap-4" data-id="element-1778" data-mp="gtrzxudhzsta">
            <div className="p-3 bg-white border border-slate-200 rounded text-slate-900 text-sm font-medium" data-id="element-1779" data-mp="gsqecvyuvnxb">
              Passes (15.7:1)
            </div>
            <div className="p-3 bg-white border border-slate-200 rounded text-slate-500 text-sm font-medium" data-id="element-1780" data-mp="pvhqkxzk2nq0">
              Passes (4.6:1)
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-1781" data-mp="2rm2yc7sa2pto">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center" data-id="element-1782" data-mp="2rofhgo9rv68b">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2" data-id="element-1783" data-mp="2nmc4fv7gftkx" />
            Keyboard Navigation
          </h3>
          <p className="text-sm text-slate-600" data-id="element-1784" data-mp="2dix87up247ag">
            All interactive elements (buttons, links, form inputs) must be
            reachable via the Tab key. Focus states must be clearly visible. We
            use Tailwind's <code data-id="element-1785" data-mp="28q44na5br4zy">focus:ring-2 focus:ring-slate-100</code> or
            similar to provide clear focus indicators without relying solely on
            color.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-1786" data-mp="1tv6acbqm85g8">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center" data-id="element-1787" data-mp="1yt7uz13owctt">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2" data-id="element-1788" data-mp="1ktgkvbgangl3" />
            Semantic HTML & ARIA
          </h3>
          <p className="text-sm text-slate-600" data-id="element-1789" data-mp="1pri3xwdpzfwk">
            Use native HTML elements (<code data-id="element-1790" data-mp="3f77sy6iz5sef">&lt;button&gt;</code>,{' '}
            <code data-id="element-1791" data-mp="3f4btueve4xoe">&lt;nav&gt;</code>, <code data-id="element-1792" data-mp="3k4zm13vcvkk7">&lt;main&gt;</code>) wherever
            possible. When building custom components (like tabs or toggles),
            appropriate ARIA roles and attributes (e.g.,{' '}
            <code data-id="element-1793" data-mp="3dp0r5psudky7">role="switch"</code>, <code data-id="element-1794" data-mp="3dor8yoe1gi32">aria-checked</code>) must be
            applied.
          </p>
        </div>
      </div>
    </div>;
}